import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, PartyPopper, Users, CalendarHeart, Sparkles, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import OliveBranch from "../components/OliveBranch";

const EVENTS_HERO =
  "https://customer-assets.emergentagent.com/job_quattro-stelle/artifacts/g1eqptl8_WhatsApp%20Image%202026-06-24%20at%2014.40.03.jpeg";
const EVENTS_SIDE =
  "https://customer-assets.emergentagent.com/job_quattro-stelle/artifacts/dnkykuqs_WhatsApp%20Image%202026-06-24%20at%2014.40.02%20%281%29.jpeg";

const TYPE_ICONS = [PartyPopper, CalendarHeart, Users, Sparkles];

const initialForm = { name: "", email: "", phone: "", occasion: "", date: "", guests: "10", message: "" };

export default function EventsPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const e = t.events;
  const [form, setForm] = useState({ ...initialForm, occasion: e.occasions[0] });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => window.scrollTo(0, 0), []);

  const update = (k) => (ev) => setForm((p) => ({ ...p, [k]: ev.target.value }));

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/events", { ...form, guests: parseInt(form.guests, 10) });
      setDone(true);
      toast.success(e.form.success);
      setForm({ ...initialForm, occasion: e.occasions[0] });
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error(e.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-qs-bg border border-qs-border rounded-lg px-4 py-3 font-outfit text-qs-text placeholder:text-qs-text2/60 focus:outline-none focus:border-qs-gold transition-colors";

  return (
    <div className="bg-qs-bg">
      {/* Header */}
      <section className="relative pt-36 pb-20 px-5 md:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={EVENTS_HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative max-w-3xl mx-auto text-center flex flex-col items-center">
          <p className="font-outfit text-qs-gold tracking-[0.35em] uppercase text-xs mb-4">{e.eyebrow}</p>
          <h1 className="font-cormorant text-white text-4xl sm:text-5xl lg:text-6xl">{e.title}</h1>
          <OliveBranch className="w-28 text-qs-gold my-5" />
          <p className="font-outfit text-white/80 max-w-xl">{e.subtitle}</p>
        </div>
      </section>

      {/* Intro + occasions */}
      <section className="py-20 md:py-24 px-5 md:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal className="relative">
            <img src={EVENTS_SIDE} alt="" className="w-full h-[360px] md:h-[480px] object-cover shadow-2xl" />
          </Reveal>
          <div>
            <Reveal>
              <p className="font-outfit text-qs-red tracking-[0.3em] uppercase text-xs mb-4">{e.typesTitle}</p>
              <h2 className="font-cormorant text-3xl sm:text-4xl text-qs-text leading-tight">{e.title}</h2>
              <OliveBranch className="w-24 text-qs-gold my-6" />
              <p className="font-outfit text-qs-text2 text-base md:text-lg leading-relaxed">{e.intro}</p>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-6 mt-10">
              {e.types.map((ty, i) => {
                const Icon = TYPE_ICONS[i % TYPE_ICONS.length];
                return (
                  <Reveal key={i} delay={120 + i * 90}>
                    <div className="group" data-testid={`event-type-${i}`}>
                      <Icon className="text-qs-gold mb-3" size={26} strokeWidth={1.4} />
                      <h3 className="font-cormorant text-xl text-qs-text mb-1">{ty.t}</h3>
                      <p className="font-outfit text-sm text-qs-text2 leading-relaxed">{ty.d}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry form */}
      <section className="pb-24 md:pb-32 px-5 md:px-10">
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <form onSubmit={handleSubmit} data-testid="event-form" className="bg-qs-bg2 p-7 md:p-10 border border-qs-border">
              <h3 className="font-cormorant text-2xl text-qs-text mb-1">{e.formTitle}</h3>
              <div className="gold-divider my-4" />

              <div className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{e.form.name}</label>
                  <input data-testid="event-name" required value={form.name} onChange={update("name")} placeholder={e.form.namePh} className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">E-Mail</label>
                    <input data-testid="event-email" type="email" required value={form.email} onChange={update("email")} placeholder={e.form.emailPh} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{e.form.phone}</label>
                    <input data-testid="event-phone" required value={form.phone} onChange={update("phone")} placeholder={e.form.phonePh} className={inputCls} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{e.form.occasion}</label>
                    <select data-testid="event-occasion" value={form.occasion} onChange={update("occasion")} className={inputCls}>
                      {e.occasions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{e.form.date}</label>
                    <input data-testid="event-date" type="date" value={form.date} onChange={update("date")} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{e.form.guests}</label>
                    <input data-testid="event-guests" type="number" min="1" required value={form.guests} onChange={update("guests")} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-qs-text2 mb-1.5">{e.form.message}</label>
                  <textarea data-testid="event-message" rows={4} value={form.message} onChange={update("message")} placeholder={e.form.messagePh} className={inputCls} />
                </div>
              </div>

              <button
                data-testid="event-submit-button"
                type="submit"
                disabled={submitting || done}
                className="mt-6 w-full flex items-center justify-center gap-2 bg-qs-red text-white hover:bg-qs-reddark transition-colors rounded-full py-4 text-xs font-semibold tracking-[0.14em] uppercase disabled:opacity-70"
              >
                {done ? (
                  <><Check size={16} /> {e.form.success.split("!")[0]}!</>
                ) : submitting ? (
                  e.form.submitting
                ) : (
                  <><Send size={15} /> {e.form.submit}</>
                )}
              </button>
            </form>
          </Reveal>

          <div className="flex justify-center mt-10">
            <button
              data-testid="events-back-btn"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 border border-qs-border text-qs-text rounded-full px-8 py-4 text-xs font-semibold tracking-[0.14em] uppercase hover:border-qs-gold transition-colors"
            >
              <ArrowLeft size={16} /> {e.back}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
